import { AbstractSelection } from 'components/system/classes/selection';
import { A9DataProviderHelper } from 'components/system/data-provider';
import { IFilter } from 'components/system/data-provider/models';
import { CustomerFilter } from '../types/CustomerFilter';

export interface ISelection {
    id: number;
    objects: {
        id: number;
        customerId: number;
    }[];
}

export class CustomerSelection extends AbstractSelection<any> {
    private _$http: angular.IHttpService;
    private $timeout;
    private static selectionId;
    private static selectionCleared = false;

    constructor(
        private $injector: angular.auto.IInjectorService,
        id = 0
    ) {
        super(id);
        this.$timeout = $injector.get<angular.ITimeoutService>('$timeout');
        this._$http = $injector.get<angular.IHttpService>('$http');

        this.update();
    }

    public waitForLoaded() {
        return this._queue(() => {
            return Promise.resolve();
        });
    }

    public getAll() {
        return this._getDetails(this.id);
    }

    public copy(oldId) {
        const oldItems: {
            id: number;
        }[] = [];

        const promise = this._getDetails(oldId)
            .then(details => {
                details.objects.forEach(value => {
                    oldItems.push({ id: value.customerId });
                });

                return this._modify(oldItems, []);
            })
            .catch(e => {
                console.error(`Couldn't get details for selection ${this._id}:`, e);
            });

        return this._queue(() => {
            return promise;
        });
    }

    public update() {
        if (CustomerSelection.selectionCleared) {
            this._id = 0;
        }
        if (!this._id) {
            this._size = 0;
            CustomerSelection.selectionCleared = false;
            return Promise.resolve();
        }
        return this._queue(() => {
            return this._getDetails(this._id).then(details => {
                this._updateState(details.id, details.objects.length);
            });
        });
    }

    public isSelected(item) {
        return !!item.selected;
    }

    public setSelection(item, value) {
        (<any>item).selected = value;
    }

    public get global() {
        return true;
    }

    public async getSelectedItems() {
        if (this._id) {
            return this._getDetails(this._id).then(details => details.objects.map(object => object.customerId));
        } else {
            return Promise.resolve([]);
        }
    }

    protected _modify(select, deselect): Promise<void> {
        return this._getSelectionId().then(id => {
            const payload = { excludes: [], includes: [] };
            if (select.length > 0) {
                payload.includes = select.map(item => item[this._idField]).filter(item => item);
            }
            if (deselect.length > 0) {
                payload.excludes = deselect.map(item => item[this._idField]).filter(item => item);
            }
            return <Promise<void>>this._$http.put(`middle://rs/selection/customers/${id}/change`, payload).then(response => {
                const details = <ISelection>response.data;
                this._updateState(details.id, details.objects.length);
            });
        });
    }

    public inverted() {
        return this._queue(() => this._createInvertedSelection());
    }

    private _createEmptySelection() {
        return <Promise<void>>this._$http.post('middle://rs/selection/customers', { name: '' }).then(response => {
            const details = <ISelection>response.data;
            this._updateState(details.id, 0);
        });
    }

    private _createInvertedSelection() {
        return <Promise<void>>this._$http.post('middle://rs/selection/customers', { name: '', inverted: true }).then(response => {
            const details = <ISelection>response.data;
            this._updateState(details.id, details.objects.length);
        });
    }

    private _excludeByFilter(filter: CustomerFilter) {
        return <Promise<void>>this._$http.put(`middle://rs/selection/customers/${this._id}/exclude-by-filter`, filter).then(response => {
            const details = <ISelection>response.data;
            this._updateState(details.id, details.objects.length);
        });
    }

    public excludeByFilter(filter: CustomerFilter): Promise<void> {
        return this._queue(() => this._excludeByFilter(filter));
    }

    private createFilterBasedSelectionRequestOnly(filter: IFilter | null) {
        const params = {};
        const helper = new A9DataProviderHelper();
        filter && helper.addFilterParameters(params, filter);
        return <Promise<number>>this._$http.post('middle://rs/selection/customers/filter', params).then(response => {
            const selection = <ISelection>response.data;
            return selection.id;
        });
    }

    private _createFilterBasedSelection(filter: IFilter | null) {
        return this.createFilterBasedSelectionRequestOnly(filter)
            .then(id => {
                return this._getDetails(id);
            })
            .then(selection => {
                this._updateState(selection.id, selection.objects.length);
            });
    }

    protected _selectAll(filter: IFilter | null): Promise<void> {
        if (filter.bySelectionId && filter.selectionId) {
            return Promise.resolve();
        }

        return this._remove().then(() => {
            return this._createFilterBasedSelection(filter);
        });
    }

    protected _deselectAll() {
        return this._remove().then(() => {
            return this._createEmptySelection();
        });
    }

    protected _invertAll(filter: IFilter) {
        const params: any = {},
            helper = new A9DataProviderHelper();

        helper.addFilterParameters(params, filter);
        params.selectionId = this.id;

        if (params.bySelectionId) {
            delete params.bySelectionId;
        }

        return <Promise<void>>this._$http
            .post('middle://rs/selection/customers/filter-inverted-selection', params)
            .then((response: any) => {
                const selection = <ISelection>response.data;
                return this._getDetails(selection.id);
            })
            .then(selection => {
                this._updateState(selection.id, selection.objects.length);
            });
    }

    protected _remove() {
        if (this.id) {
            this.emit('remove');
            return <Promise<void>>this._$http.delete(`middle://rs/selection/customers/${this.id}`).then(() => {
                this._id = null;
                this._size = 0;
            });
        }
        return Promise.resolve();
    }

    private _getDetails(id) {
        return <Promise<any>>this._$http.get<ISelection>(`middle://rs/selection/customers/${id}/details`).then(response => response.data);
    }

    private _getSelectionId() {
        if (this._id) {
            return Promise.resolve(this._id);
        }
        return this._createEmptySelection().then(() => this._id);
    }

    public static setSelectionId(id) {
        return (CustomerSelection.selectionId = id);
    }
    public static getSelectionId() {
        return CustomerSelection.selectionId;
    }
    public static clearSelection() {
        CustomerSelection.selectionCleared = true;
    }
}
